/**
 * PairedDevicesCard — shows the devices currently paired to the LOGGED-IN
 * user's account. Mobile devices pair automatically on first mobile-app
 * login. Browser devices pair via the "Pair this browser" button below.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIRM_ADMIN = ["firm_owner", "managing_partner"];

// ----- Web Push helpers -----
function urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

async function pairThisBrowser() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("This browser does not support Web Push (try Chrome / Edge / Firefox on desktop or Android, or Safari 16.4+ on iOS 16.4+).");
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
        throw new Error("Notification permission was " + perm + ". Enable notifications for this site in your browser settings, then click again.");
    }
    const reg = await navigator.serviceWorker.register("/service-worker.js");
    await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/public-key");
    const vapid = data?.key;
    if (!vapid) throw new Error("Server hasn't generated a VAPID public key yet. Try again in a moment.");
    let sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();   // re-create with fresh server key
    sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid),
    });
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
    return sub;
}

async function unpairThisBrowser() {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    try { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch (e) { /* best-effort */ }
    await sub.unsubscribe();
}


export default function PairedDevicesCard() {
    const { user } = useAuth();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [roster, setRoster] = useState(null);
    const [rosterOpen, setRosterOpen] = useState(false);
    const [pairing, setPairing] = useState(false);
    const [testing, setTesting] = useState(false);
    const isFirmAdmin = FIRM_ADMIN.includes(user?.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tenant/push/devices");
            setDevices(data?.devices || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    const loadRoster = useCallback(async () => {
        try {
            const { data } = await api.get("/tenant/push/devices/firm-roster");
            setRoster(data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const rename = async (id) => {
        const name = editName.trim();
        if (!name) { toast.error("Name cannot be empty"); return; }
        try {
            await api.patch(`/tenant/push/devices/${id}`, { name });
            toast.success("Device renamed");
            setEditingId(null);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const unpair = async (id, name, platform) => {
        if (!window.confirm(`Unpair ${name || "this device"}?\n\nPush notifications will stop until you re-pair.`)) return;
        try {
            if (platform === "web") {
                // Also tear down the browser-side subscription
                await unpairThisBrowser().catch(() => {});
            }
            await api.delete(`/tenant/push/devices/${id}`);
            toast.success("Device unpaired");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const handlePairBrowser = async () => {
        setPairing(true);
        try {
            await pairThisBrowser();
            toast.success("This browser is now paired. You'll receive notifications here.");
            await load();
        } catch (e) {
            toast.error(e?.message || apiError(e), { duration: 10000 });
        } finally { setPairing(false); }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const r = await api.post("/tenant/push/test");
            const note = r.data?.note;
            if (note) {
                toast.message("Test push attempted", { description: note, duration: 8000 });
            } else {
                toast.success(`Test push fired to ${r.data?.devices || 0} device(s). Check for the system notification now.`, { duration: 8000 });
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setTesting(false); }
    };

    return (
        <div className="bg-muted border border-border p-6 space-y-5" data-testid="paired-devices-card">
            {/* Header */}
            <div className="flex items-start gap-3">
                <Smartphone className="w-6 h-6 text-primary mt-0.5" strokeWidth={1.5}/>
                <div className="flex-1">
                    <div className="text-sm text-foreground">Paired devices</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Devices that receive YOUR push notifications. Each notification is delivered only to the recipient — no cross-user leakage.
                    </div>
                </div>
            </div>

            {/* Pair this browser + Send test */}
            <div className="flex flex-wrap items-center gap-3 bg-background border border-border p-3">
                <button onClick={handlePairBrowser} disabled={pairing}
                    data-testid="pair-this-browser-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                    {pairing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Globe className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                    {pairing ? "Pairing…" : "Pair this browser"}
                </button>
                <button onClick={sendTest} disabled={testing || devices.length === 0}
                    data-testid="send-test-push-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border text-muted-foreground text-xs uppercase tracking-[0.16em] hover:border-primary hover:text-primary disabled:opacity-30">
                    {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                    {testing ? "Sending…" : "Send test notification"}
                </button>
                <span className="text-[10px] text-muted-foreground flex-1 min-w-0">
                    {devices.length === 0
                        ? "Tip — also install the LegalOS mobile app to receive pushes on your phone."
                        : `${devices.length} device${devices.length === 1 ? "" : "s"} paired.`}
                </span>
            </div>

            {/* How to pair — only show if user has zero devices */}
            {!loading && devices.length === 0 && (
                <div className="border border-amber-500/40 bg-amber-500/5 p-4 space-y-2" data-testid="pair-instructions">
                    <div className="flex items-center gap-2 text-amber-400 text-xs uppercase tracking-[0.16em]">
                        <BellOff className="w-4 h-4" strokeWidth={1.5}/> No device paired yet
                    </div>
                    <div className="text-[11px] text-muted-foreground space-y-1">
                        <div><span className="text-foreground">Browser:</span> click &quot;Pair this browser&quot; above → approve the permission prompt.</div>
                        <div><span className="text-foreground">Mobile:</span> install LegalOS app → sign in same email → approve permission → device auto-pairs.</div>
                    </div>
                </div>
            )}

            {/* Device list */}
            {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
            {!loading && devices.length > 0 && (
                <div className="space-y-2" data-testid="paired-devices-list">
                    {devices.map(d => (
                        <div key={d.id} className="bg-background border border-border p-3 flex items-center gap-3"
                            data-testid={`paired-device-${d.id}`}>
                            {d.platform === "web" ? <Globe className="w-5 h-5 text-primary" strokeWidth={1.5}/> : <PhoneIcon className="w-5 h-5 text-primary" strokeWidth={1.5}/>}
                            <div className="flex-1 min-w-0">
                                {editingId === d.id ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text" value={editName} maxLength={60}
                                            onChange={e => setEditName(e.target.value)}
                                            onKeyDown={e => { if (e.key === "Enter") rename(d.id); if (e.key === "Escape") setEditingId(null); }}
                                            autoFocus
                                            className="flex-1 bg-muted border border-border px-2 py-1 text-xs focus:border-primary outline-none"
                                            data-testid={`paired-device-rename-input-${d.id}`}/>
                                        <button onClick={() => rename(d.id)} className="text-emerald-400 text-[10px] uppercase tracking-[0.16em]">Save</button>
                                        <button onClick={() => setEditingId(null)} className="text-muted-foreground text-[10px] uppercase tracking-[0.16em]">Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-sm text-foreground">{d.name}</div>
                                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                            {d.platform || "—"}
                                            {d.last_seen_at && <> · last active {fmtDateTime(d.last_seen_at)}</>}
                                            {d.paired_at && <> · paired {fmtDate(d.paired_at)}</>}
                                        </div>
                                    </>
                                )}
                            </div>
                            {editingId !== d.id && (
                                <>
                                    <button onClick={() => { setEditingId(d.id); setEditName(d.name); }}
                                        title="Rename device" aria-label="rename"
                                        data-testid={`paired-device-rename-${d.id}`}
                                        className="p-1.5 text-muted-foreground hover:text-foreground">
                                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                    </button>
                                    <button onClick={() => unpair(d.id, d.name, d.platform)}
                                        title="Unpair device" aria-label="unpair"
                                        data-testid={`paired-device-unpair-${d.id}`}
                                        className="p-1.5 text-muted-foreground hover:text-red-400">
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Isolation reminder */}
            <div className="flex items-start gap-2 text-[10px] text-muted-foreground border-t border-border pt-3">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={1.5}/>
                <span>
                    Strict per-user isolation: each push is delivered only to the recipient&apos;s paired devices.
                    A firm owner cannot see another user&apos;s notifications, and vice-versa. Audit logged.
                </span>
            </div>

            {/* Firm-wide roster (admin only) */}
            {isFirmAdmin && (
                <div className="border-t border-border pt-4">
                    <button onClick={() => { setRosterOpen(o => !o); if (!roster) loadRoster(); }}
                        data-testid="firm-roster-toggle"
                        className="text-xs text-primary uppercase tracking-[0.16em] hover:underline inline-flex items-center gap-2">
                        <Bell className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        {rosterOpen ? "Hide" : "Show"} firm-wide pairing roster (admin)
                    </button>
                    {rosterOpen && (
                        <div className="mt-3 space-y-2" data-testid="firm-roster">
                            {!roster && <div className="text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline-block"/> Loading roster…</div>}
                            {roster && (
                                <>
                                    <div className="text-[11px] text-muted-foreground">
                                        {roster.total_users} users in firm · {roster.total_devices} devices paired · {roster.unpaired_users} users haven&apos;t paired yet
                                    </div>
                                    <div className="overflow-x-auto bg-background border border-border">
                                        <table className="w-full text-[11px]">
                                            <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em] text-[10px]">
                                                <tr>
                                                    <th className="text-left p-2">User</th>
                                                    <th className="text-left p-2">Role</th>
                                                    <th className="text-right p-2">Devices</th>
                                                    <th className="text-left p-2">Platforms</th>
                                                    <th className="text-left p-2">Last seen</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(roster.users || []).map(u => (
                                                    <tr key={u.user_id} className="border-t border-border" data-testid={`firm-roster-row-${u.user_id}`}>
                                                        <td className="p-2"><div className="text-foreground">{u.name}</div><div className="text-muted-foreground">{u.email}</div></td>
                                                        <td className="p-2 text-muted-foreground">{u.role}</td>
                                                        <td className={`p-2 text-right font-mono ${u.device_count === 0 ? "text-red-400" : "text-emerald-400"}`}>{u.device_count}</td>
                                                        <td className="p-2 text-muted-foreground">{(u.platforms || []).join(", ") || "—"}</td>
                                                        <td className="p-2 text-muted-foreground font-mono text-[10px]">{u.last_seen_at ? fmtDateTime(u.last_seen_at) : "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
